// Shared sales-pipeline filtering for the two lead databases.
//
// Spanish clubs live in `teamDatabase` and British ones in `englishClubLeads`,
// under different field names, but staff work them the same way: UK → Spain
// sells Spain tours to British clubs and schools, Spain → UK sells UK tours to
// Spanish ones. Both views therefore normalise a record into the shape read by
// `matches()` below and render the same filter bar, so a salesperson moving
// between the two never has to relearn the controls.

import { useState } from 'react';

export const LEAD_STATUSES = ['New', 'Contacted', 'Follow-up', 'Quoted', 'Won', 'Not interested'];

export const ENTITY_TYPE_LABELS = { school: 'School', club: 'Club', venue: 'Sports centre' };

export const CONTACT_OPTIONS = [
  ['yes', 'Contact details present'],
  ['no',  'No contact details yet'],
];

export const ORIGIN_OPTIONS = [
  ['client',  'Past tour clients'],
  ['manual',  'Added by hand'],
  ['scraped', 'Scraped teams'],
];

// A record is "scraped" when a bulk import created it, and a "client" when it
// was mirrored in from a portal customer who has actually toured with us. The
// two databases tag these differently, so read both spellings.
export function recordOrigin(rec) {
  if (rec?.origin === 'scraped' || rec?.source === 'scraped') return 'scraped';
  if (rec?.origin === 'portal-customer') return 'client';
  return 'manual';
}

export function useSalesFilters(currentUid) {
  const [search, setSearch]                 = useState('');
  const [status, setStatus]                 = useState('');
  const [assignee, setAssignee]             = useState('');
  const [origin, setOrigin]                 = useState('');
  const [contact, setContact]               = useState('');
  const [entityType, setEntityType]         = useState('');
  const [myLeadsOnly, setMyLeadsOnly]       = useState(false);
  const [consentOnly, setConsentOnly]       = useState(false);
  // Scraped imports outnumber the clubs staff actually know about many times
  // over, so both databases open on the worked list and let you opt in.
  const [includeScraped, setIncludeScraped] = useState(false);

  // `rec` is the normalised view of a record:
  //   { status, assignedTo, origin, entityType, hasContact, marketingConsent, haystack }
  function matches(rec) {
    // The tick-box is the default gate, but picking "Scraped teams" from the
    // source list is itself a deliberate request to see them — asking staff to
    // do both would just look broken.
    if (rec.origin === 'scraped' && !includeScraped && origin !== 'scraped') return false;
    if (origin && rec.origin !== origin) return false;
    if (status && rec.status !== status) return false;
    if (assignee && rec.assignedTo !== assignee) return false;
    if (myLeadsOnly && rec.assignedTo !== currentUid) return false;
    if (entityType && rec.entityType !== entityType) return false;
    if (contact && rec.hasContact !== (contact === 'yes')) return false;
    if (consentOnly && !rec.marketingConsent) return false;
    const q = search.trim().toLowerCase();
    if (q && !(rec.haystack || '').toLowerCase().includes(q)) return false;
    return true;
  }

  return {
    search, setSearch, status, setStatus, assignee, setAssignee,
    origin, setOrigin, contact, setContact, entityType, setEntityType,
    myLeadsOnly, setMyLeadsOnly, consentOnly, setConsentOnly,
    includeScraped, setIncludeScraped, matches,
  };
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white"
    >
      {children}
    </select>
  );
}

function Check({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-[#374151] font-medium px-1 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      {children}
    </label>
  );
}

// The Spanish and British databases render this bar identically. The two opt-in
// props are for records that simply do not carry the field: only Spanish clubs
// register themselves through a form with a marketing tick-box, and Padelante
// leads are individuals, so they have neither an entity type nor a scraped
// import behind them.
export function SalesFilterBar({ f, staff, statusLabel = 'Any sales status', showConsent = false, showEntityType = true, showScraped = true }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      <div className="col-span-2 sm:col-span-1">
        <input
          value={f.search} onChange={(e) => f.setSearch(e.target.value)} placeholder="Search…"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        />
      </div>
      <Sel value={f.status} onChange={(e) => f.setStatus(e.target.value)}>
        <option value="">{statusLabel}</option>
        {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
      </Sel>
      <Sel value={f.assignee} onChange={(e) => f.setAssignee(e.target.value)}>
        <option value="">Anyone assigned</option>
        {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
      </Sel>
      <Sel value={f.origin} onChange={(e) => f.setOrigin(e.target.value)}>
        <option value="">Any source</option>
        {ORIGIN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Sel>
      <Sel value={f.contact} onChange={(e) => f.setContact(e.target.value)}>
        <option value="">Any contact details</option>
        {CONTACT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Sel>
      {showEntityType && (
        <Sel value={f.entityType} onChange={(e) => f.setEntityType(e.target.value)}>
          <option value="">Club, school or centre</option>
          {Object.entries(ENTITY_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Sel>
      )}
      <Check checked={f.myLeadsOnly} onChange={f.setMyLeadsOnly}>My leads only</Check>
      {showScraped && <Check checked={f.includeScraped} onChange={f.setIncludeScraped}>Include scraped teams</Check>}
      {showConsent && <Check checked={f.consentOnly} onChange={f.setConsentOnly}>Marketing opt-in</Check>}
    </div>
  );
}
